import React from 'react';
import { DeliveryWindow } from '@bringg/types';
import classNames from 'classnames';

import SelectableList from '../../selectable-list/selectable-list';
import { ScreenType } from '../../../types/common.consts';
import { DateInfo } from '../delivery-window-calendar.types';
import getFormatDate from '../helpers/get-format-date';
import Divider from '../../divider/divider';
import { TEST_IDS } from '../test-ids';

import styles from './time-window-selection.module.scss';

type TimeWindowSelectionProps = {
	dates: DateInfo[];
	currentDate: string;
	locale: string;
	onTimeWindowSelected: (timeWindowId: DeliveryWindow) => void;
	screenType?: ScreenType;
	externalIdInfo?: string;
	timezone?: string;
	schedulingCustomerMessage?: JSX.Element;
};

const TimeWindowSelection = ({
	dates,
	currentDate,
	locale,
	onTimeWindowSelected,
	screenType = ScreenType.DESKTOP,
	externalIdInfo,
	timezone,
	schedulingCustomerMessage
}: TimeWindowSelectionProps) => {
	const onSelect = (selectedDeliveryWindow: DateInfo) => {
		onTimeWindowSelected(selectedDeliveryWindow?.deliveryWindow);
	};

	const screenTypeClass = screenType === ScreenType.MOBILE ? styles.mobile : styles.desktop;

	return (
		<div className={classNames(styles.timeWindowSelectionContainer, screenTypeClass)}>
			<div className={styles.timeWindowInfo}>
				{externalIdInfo ? (
					<>
						<span className={styles.timeWindowSelectionTitle} data-test-id={TEST_IDS.externalIdInfo}>
							{externalIdInfo}
						</span>
						{screenType === ScreenType.DESKTOP ? <div className={styles.divider}></div> : null}
					</>
				) : null}
				<span
					className={styles.timeWindowSelectionTitle}
					data-test-id={`selected-date-${getFormatDate({
						date: currentDate,
						format: 'DD-MM-YYYY',
						locale
					})}`}
				>
					{getFormatDate({ date: currentDate, format: 'dddd MMMM D YYYY', locale })}
				</span>
				{timezone ? (
					<>
						{screenType === ScreenType.DESKTOP ? <div className={styles.divider}></div> : null}
						<span className={styles.timeWindowSelectionTitle} data-test-id={TEST_IDS.customerTimezone}>
							{timezone}
						</span>
					</>
				) : null}
			</div>
			{schedulingCustomerMessage ? (
				<div
					className={classNames(styles.schedulingCustomerMessage, {
						[styles.mobileMargin]: screenType === ScreenType.MOBILE
					})}
					data-test-id={TEST_IDS.schedulingCustomerMessage}
				>
					{schedulingCustomerMessage}
				</div>
			) : null}
			{screenType === ScreenType.DESKTOP ? <Divider className={styles.desktopDivider} /> : null}
			<SelectableList
				className={classNames(styles.timeWindowSelectionItemsContainer, screenTypeClass)}
				itemClassName={classNames(styles.timeWindowSelectionSingleItem, screenTypeClass)}
				items={dates}
				onSelect={onSelect}
				renderValue={(deliveryWindow: DateInfo) => <>{deliveryWindow.text}</>}
				allowDeselect
			/>
		</div>
	);
};

export default TimeWindowSelection;
